'use client';

import TrySeozastCTA from '@/app/components/TrySeozastCTA';
import Image from 'next/image';
import Head from 'next/head';
import Link from 'next/link';

const Page = () => {
    const canonicalUrl = 'https://seozast.com/articles/ai-seo-in-2025';

    return (
        <div className="max-w-4xl mx-auto px-6 py-16">
            <Head>
                <title>AI SEO in 2025: How to Make Your Content AI-Citation Ready</title>
                <link rel="canonical" href={canonicalUrl} />
                {/* JSON-LD Article metadata */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": [
                            {
                                "@type": "Question",
                                "name": "Which AI bots should I allow?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Include GPTBot, ClaudeBot, PerplexityBot, and any other relevant LLM crawlers. Avoid blocking them with noindex tags."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "Can AI SEO increase my traffic?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Yes. AI SEO makes your content more likely to be cited, driving referral traffic from AI-powered search engines and chatbots."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "Do I still need traditional SEO?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Absolutely. AI SEO complements standard SEO. Structured, credible content helps both humans and AI find and trust your content."
                                }
                            },
                            {
                                "@type": "Question",
                                "name": "What makes content AI-citation ready?",
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": "Content must be crawlable, structured with clear headings, factual, include author and date, use metadata, and provide references. Media should be machine-readable."
                                }
                            }
                        ]
                    })}
                </script>
            </Head>

            <section className="mb-16">
                <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4 tracking-tight">
                    AI SEO in 2025: How to Make Your Content AI-Citation Ready
                </h1>

                <div className="text-sm text-neutral-500 mb-8 flex items-center space-x-3">
                    <time dateTime="2025-10-27">October 27, 2025</time>
                    <span className="text-neutral-300">•</span>
                    <span>By SEOzast Team</span>
                    <span className="text-neutral-300">•</span>
                    <span>7 min read</span>
                </div>

                {/* Imagen con figure + figcaption */}
                <figure className="relative w-full h-[300px] md:h-[450px] mb-10 rounded-xl overflow-hidden shadow-xl">
                    <Image
                        src="https://images.pexels.com/photos/1181675/pexels-photo-1181675.jpeg?auto=compress&cs=tinysrgb&h=650&w=940"
                        alt="AI SEO Best Practices"
                        fill
                        className="object-cover"
                        priority
                    />
                    <figcaption className="absolute bottom-2 right-2 text-xs text-white bg-black/50 px-2 py-1 rounded">
                        Photo by Pixabay / Pexels
                    </figcaption>
                </figure>

                <div className="article-content space-y-8 text-neutral-800 leading-relaxed font-sans">
                    <p>
                        AI-powered search engines are changing the rules of SEO. From ChatGPT to Perplexity and Claude, <strong>content now needs to be AI-citation ready</strong> to appear in answers and recommendations. In 2025, this is no longer optional — it's essential.
                    </p>

                    <h2 className="text-2xl font-semibold">What Does “AI-Citation Ready” Mean?</h2>
                    <p>
                        Being AI-citation ready means your content is structured, credible, and crawlable by AI bots. This allows AI tools to understand, reference, and display your content in real-time answers.
                    </p>

                    <h2 className="text-2xl font-semibold">Key Steps to Optimize for AI SEO</h2>
                    <ol className="list-decimal pl-6 space-y-2">
                        <li>
                            <strong>Allow AI bots:</strong> Add GPTBot, PerplexityBot, ClaudeBot to your <code>robots.txt</code> and avoid <code>noindex</code> or <code>nosnippet</code> tags.
                        </li>
                        <li>
                            <strong>Use clear structure:</strong> Headings (H2, H3) with one idea per section. Prefer Q&A formats with direct answers first.
                        </li>
                        <li>
                            <strong>Provide metadata:</strong> Author, date, and external references make your content trustworthy. Example reference: <a href="https://www.microsoft.com/en-us/bing/indexnow" className="text-emerald-600 hover:underline">IndexNow Official Docs</a>.
                        </li>
                        <li>
                            <strong>Pillar-cluster content:</strong> Build topic coverage by linking overview pages (pillars) to detailed subtopics (clusters).
                        </li>
                        <li>
                            <strong>Internal linking:</strong> Use descriptive anchor text under relevant headings to help AI understand context.
                        </li>
                        <li>
                            <strong>Machine-readable media:</strong> Use <code>&lt;figure&gt;</code> and <code>&lt;figcaption&gt;</code>, tables in HTML, and meaningful alt text.
                        </li>
                    </ol>

                    <h2 className="text-2xl font-semibold">Why AI SEO Matters</h2>
                    <p>
                        AI tools now generate answers based on real-time, structured, and reliable content. If your content is not AI-ready:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                        <li>It may not appear in AI-generated answers.</li>
                        <li>It could be considered outdated or irrelevant.</li>
                        <li>You miss referral traffic from AI search platforms.</li>
                    </ul>

                    <h2 className="text-2xl font-semibold">How SEOzast Helps</h2>
                    <p>
                        With <a href="https://seozast.com" className="text-emerald-600 hover:underline">SEOzast</a>, you can:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                        <li>Auto-generate structured, AI-ready content</li>
                        <li>Deploy articles that ping AI bots instantly</li>
                        <li>Include metadata and references automatically</li>
                        <li>Get free, high-quality Pexels images for every post</li>
                    </ul>

                    <div className="bg-neutral-100 p-6 rounded-lg shadow-sm text-center">
                        <h3 className="text-lg font-semibold mb-4">Make Your Content AI-Citation Ready Today</h3>
                        <TrySeozastCTA />
                    </div>

                    {/* Related Articles */}
                    <h2 className="text-2xl font-semibold mt-12 mb-6">Related Articles</h2>
                    <ul className="list-disc pl-6 space-y-2 text-emerald-600">
                        <li>
                            <Link href="/articles/ai-seo-vs-traditional-seo-2025" className="hover:underline">
                                AI SEO vs Traditional SEO in 2025
                            </Link>
                        </li>
                        <li>
                            <Link href="/articles/get-found-in-chatgpt-and-perplexity" className="hover:underline">
                                Get Found in ChatGPT and Perplexity
                            </Link>
                        </li>
                        <li>
                            <Link href="/articles/seozast-seo-automation-easy-affordable" className="hover:underline">
                                SEOzast: SEO Automation Made Easy & Affordable
                            </Link>
                        </li>
                    </ul>
                </div>
            </section>
        </div>
    );
};

export default Page;
